/* eslint-disable no-empty */
import type { FirebaseApp } from "firebase/app";
import { initializeApp } from "firebase/app";
import type { Firestore } from "firebase/firestore";
import { initializeFirestore } from "firebase/firestore";
import {
  getAuth,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  type Auth,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "AIzaSyDKL9SxUpj-jvTL7pi2uObxDSrI3c0pkvY",
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "studio-8849194767-f8721.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "studio-8849194767-f8721",
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "studio-8849194767-f8721.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "693973559209",
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "1:693973559209:web:ab782b1ab39229f686c6b8",
};

const requiredConfigKeys: Array<keyof typeof firebaseConfig> = ["apiKey", "projectId"];

const hasConfigValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return Boolean(value);
};

const firebaseDebugEnabled = (import.meta.env.VITE_DEBUG_FIREBASE ?? '') === '1';
const firebaseInfo = (...args: Parameters<typeof console.info>): void => {
  if (firebaseDebugEnabled) {
    console.info(...args);
  }
};

const isFirebaseConfigured = requiredConfigKeys.every((key) =>
  hasConfigValue(firebaseConfig[key])
);

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let authInstance: Auth | null = null;
let authPersistenceApplied = false;

// Allow a runtime toggle to disable Firestore from this client/tab for
// emergency debugging: set `localStorage.disableFirestore = '1'` and reload.
let firestoreDisabled = false;
try {
  // localStorage may not be available in some environments
  const envDisable = (import.meta.env.VITE_DISABLE_FIRESTORE ?? '') === '1';
  const localDisable = typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';
  firestoreDisabled = envDisable || localDisable;
  if (envDisable) {
    console.warn('[firebase] Firestore disabled via VITE_DISABLE_FIRESTORE=1');
  } else if (localDisable) {
    console.warn('[firebase] Firestore disabled via localStorage.disableFirestore=1');
  }
} catch {}

if (isFirebaseConfigured) {
  app = initializeApp(firebaseConfig);
  authInstance = getAuth(app);
  if (!firestoreDisabled) {
    db = initializeFirestore(app, {
      ignoreUndefinedProperties: true,
    });
  } else {
    console.warn('[firebase] Firestore disabled via localStorage.disableFirestore=1');
  }
}

export { app, db, isFirebaseConfigured };
export const peekAuthInstance = (): Auth | null => authInstance;

// Auto-init App Check only when explicitly enabled via env var. This prevents
// accidental initialization in dev or when a site key is present but not
// correctly configured (which can cause `firebase-app-check-token-is-invalid`).
// IMPORTANT: App Check is OFF by default - you must set VITE_ENABLE_APPCHECK=1 AND
// provide a valid VITE_FIREBASE_APPCHECK_SITE_KEY to enable it.
const enableAppCheck = (import.meta.env.VITE_ENABLE_APPCHECK === '1');
const appCheckAllowedHosts = (import.meta.env.VITE_FIREBASE_APPCHECK_ALLOWED_HOSTS ?? '')
  .split(',')
  .map((host) => host.trim())
  .filter(Boolean);

const recaptchaScriptIds = {
  enterprise: 'firebase-recaptcha-enterprise',
  v3: 'firebase-recaptcha-v3',
};
const recaptchaScriptPromises: Partial<Record<keyof typeof recaptchaScriptIds, Promise<void> | null>> = {};

const ensureRecaptchaScript = (
  siteKey: string,
  mode: keyof typeof recaptchaScriptIds
): Promise<void> => {
  if (!siteKey || typeof window === 'undefined') return Promise.resolve();
  if (recaptchaScriptPromises[mode]) return recaptchaScriptPromises[mode] as Promise<void>;

  recaptchaScriptPromises[mode] = new Promise<void>((resolve, reject) => {
    const scriptId = recaptchaScriptIds[mode];
    const existing = document.getElementById(scriptId);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src =
      mode === 'enterprise'
        ? `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`
        : `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => {
      recaptchaScriptPromises[mode] = null;
      reject(err);
    };
    document.head.appendChild(script);
  }).catch((err) => {
    console.warn('[firebase] Failed to load reCAPTCHA script:', err);
  });

  return recaptchaScriptPromises[mode] as Promise<void>;
};

const isAppCheckHostAllowed = (hostname: string): boolean => {
  if (!hostname) return false;
  if (hostname === 'thirteen.happyapps.dev') return false;
  if (appCheckAllowedHosts.length) {
    return appCheckAllowedHosts.some((allowed) => {
      if (allowed.startsWith('.')) {
        return hostname.endsWith(allowed);
      }
      return hostname === allowed;
    });
  }
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === 'galacticcalendar.lovable.app' ||
    hostname === 'galacticcalendar.lovable.dev' ||
    hostname.endsWith('.web.app') ||
    hostname.endsWith('.firebaseapp.com')
  );
};

const isSignInOnlyRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname || '';
  return pathname === '/sign-in' || pathname === '/login';
};

const clearPersistedAppCheckState = (): void => {
  if (typeof window === 'undefined') return;
  try {
    delete (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN;
  } catch {}
  try {
    window.localStorage.removeItem('firebase:appCheckDebugToken');
  } catch {}
  try {
    window.sessionStorage.removeItem('firebase:appCheckDebugToken');
  } catch {}
};

// Holds the initialized App Check instance so callers can mint tokens for
// unauthenticated backend endpoints (e.g. the branded auth-email function).
let appCheckInstance: unknown = null;

// Returns a fresh App Check token, or null if App Check isn't active on this
// route/host (e.g. sign-in pages, where App Check is intentionally skipped).
export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheckInstance) return null;
  try {
    const { getToken } = await import('firebase/app-check');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await getToken(appCheckInstance as any, false);
    return result?.token ?? null;
  } catch (e) {
    console.warn('[firebase] App Check token fetch failed (non-fatal):', e);
    return null;
  }
}

// Initialize App Check (debug token supported; falls back to reCAPTCHA v3)
export async function initAppCheck(): Promise<void> {
  if (!app || !isFirebaseConfigured || !enableAppCheck) return;
  if (isSignInOnlyRoute()) {
    clearPersistedAppCheckState();
    firebaseInfo('[firebase] App Check skipped on sign-in route');
    return;
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (!isAppCheckHostAllowed(hostname)) {
    firebaseInfo(`[firebase] App Check skipped for hostname: ${hostname || 'unknown'}`);
    return;
  }

  // Set the debug token first so the SDK can pick it up
  const debugToken = (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN ?? '').toString();
  if (typeof window !== 'undefined' && debugToken) {
    try {
      (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
      window.localStorage.setItem('firebase:appCheckDebugToken', debugToken);
      firebaseInfo('[firebase] App Check debug token set');
    } catch (e) {
      console.warn('[firebase] Could not set debug token:', e);
    }
  }

  const siteKey = (import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '').toString().trim();
  if (!siteKey && !debugToken) {
    console.warn('[firebase] App Check site key missing and no debug token; skipping App Check init');
    return;
  }

  try {
    const appCheckModule = await import('firebase/app-check');
    if (!appCheckModule.initializeAppCheck) {
      console.warn('[firebase] App Check module missing required exports');
      return;
    }

    try {
      const useEnterpriseProvider = Boolean(appCheckModule.ReCaptchaEnterpriseProvider);
      if (siteKey) {
        await ensureRecaptchaScript(siteKey, useEnterpriseProvider ? 'enterprise' : 'v3');
      }

      // When a debug token is set, the SDK uses the debug provider automatically after init.
      const provider = useEnterpriseProvider
        ? new appCheckModule.ReCaptchaEnterpriseProvider(siteKey || 'debug-key')
        : new appCheckModule.ReCaptchaV3Provider(siteKey || 'debug-key');
      appCheckInstance = appCheckModule.initializeAppCheck(app as FirebaseApp, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
      firebaseInfo('[firebase] App Check initialized');
    } catch (initErr) {
      console.warn('[firebase] App Check init failed (non-fatal):', initErr);
    }
  } catch (e) {
    console.warn('[firebase] App Check setup failed:', e);
  }
}

// Only auto-init if explicitly enabled AND site key is present
if (
  typeof window !== 'undefined' &&
  enableAppCheck &&
  import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY &&
  !isSignInOnlyRoute()
) {
  initAppCheck().catch(() => {});
} else if (typeof window !== 'undefined') {
  if (isSignInOnlyRoute()) {
    clearPersistedAppCheckState();
  }
  firebaseInfo('[firebase] App Check disabled (set VITE_ENABLE_APPCHECK=1 and VITE_FIREBASE_APPCHECK_SITE_KEY to enable)');
}

// Lazy-initialize and return the Auth instance. This uses dynamic import to
// avoid loading auth in environments that don't need it and to allow the
// emulator to be connected in development via env vars.
export async function getAuthInstance(): Promise<Auth | undefined> {
  if (!app || !isFirebaseConfigured) return undefined;
  try {
    const auth = authInstance ?? getAuth(app as FirebaseApp);
    // Ensure auth.settings exists to avoid SDK code accessing undefined.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(auth as any).settings) (auth as any).settings = {};
    } catch {}
    // Apply tenant ID from environment if provided
    const envTenant = import.meta.env.VITE_FIREBASE_AUTH_TENANT_ID ?? import.meta.env.VITE_FIREBASE_TENANT_ID;
    if (envTenant && typeof envTenant === 'string' && envTenant.trim()) {
      try {
        auth.tenantId = String(envTenant).trim();
        firebaseInfo('[firebase] auth tenant set from env:', auth.tenantId);
      } catch {}
    }
    // Favor persistent auth to avoid losing sessions on reload; fall back gently when storage is blocked.
    if (!authPersistenceApplied) {
      const candidates = [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ].filter(Boolean);
      let persistenceApplied = false;
      for (const persistence of candidates) {
        try {
          await setPersistence(auth, persistence);
          authPersistenceApplied = true;
          persistenceApplied = true;
          firebaseInfo('[firebase] Auth persistence set successfully:', persistence.type || 'persistence');
          break;
        } catch (err) {
          console.warn('[firebase] setPersistence failed, trying next option', err);
        }
      }
      if (!persistenceApplied) {
        console.error('[firebase] ⚠️ ALL auth persistence options failed - sessions will not persist on refresh!');
      }
    }
    // Connect to emulator when VITE_FIREBASE_AUTH_EMULATOR_HOST is provided
    const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
    if (emulatorHost) {
      try {
        // Ensure emulatorHost is a full ws/http URL like http://localhost:9099
        connectAuthEmulator(auth, emulatorHost, { disableWarnings: true });
        firebaseInfo(`[firebase] Auth emulator connected: ${emulatorHost}`);
      } catch {
        // ignore failures to connect emulator
      }
    }
    authInstance = auth;
    return authInstance;
  } catch {
    return undefined;
  }
}

// Allow runtime tenant switching for multi-tenant projects
export async function setAuthTenant(tenantId?: string): Promise<void> {
  const auth = await getAuthInstance();
  if (!auth) return;
  try {
    if (!tenantId) delete (auth as any).tenantId;
    else auth.tenantId = tenantId;
  } catch {}
}
